import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Typography,
  Alert,
} from '@mui/material';
import ErrorSnackbar from './ErrorSnackbar';
import { Close as CloseIcon } from '@mui/icons-material';
import { Send as SendIcon } from '@mui/icons-material';
import store from 'storeClient';
import ConsultasClient from 'legacyfrontend/api/consultas';
import { useColors } from 'legacyfrontend/hooks/useColors';

const ContactFormModal = ({
  consultaPackage,
  onClose,
  title = 'Enviá tu consulta',
  subtitle = null,
  variant = 'package', // 'package' | 'agency' | 'banner'
}) => {
  const { t } = useTranslation(['translations', 'layout']);
  const { background, text } = useColors();
  const {
    token,
    applicationId,
    paisId,
    templateData,
    nombre: nombreAgencia,
  } = store.getState().applicationState;

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    comentarios: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const occupationMapper = {
    '0100': 1,
    '0200': 2,
    '0300': 3,
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      apellido: '',
      email: '',
      telefono: '',
      comentarios: '',
    });
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    setIsSubmitting(false);
    onClose();
  };

  const handleInputChange = (field) => (event) => {
    const { value } = event.target;

    if (field === 'telefono' && value.length > 20) {
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = () => {
    const requiredFields = ['nombre', 'apellido', 'email', 'comentarios'];
    const missingFields = requiredFields.filter((field) => !formData[field]?.trim());

    if (missingFields.length > 0) {
      setError('Por favor complete todos los campos obligatorios');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Por favor ingrese un email válido');
      return false;
    }

    return true;
  };

  const preparePackageData = () => {
    if (!consultaPackage) {
      throw new Error('No hay datos del paquete disponibles');
    }

    let ciudadOrigen = '';
    let ciudadDestino = '';
    let provider = '';
    let numeroPaquete = '';
    let categoriaHabitacionPaquete = '';
    let cantidadPersonasPaquete = '';

    const isInternalPackage = consultaPackage.paqueteDestinos || consultaPackage.originCity;

    if (isInternalPackage) {
      ciudadOrigen = consultaPackage.originCity?.name || '';
      ciudadDestino = consultaPackage.paqueteDestinos?.[0]?.city?.name || '';
      provider = consultaPackage.providerName || nombreAgencia;
      numeroPaquete = consultaPackage.id?.toString() || '';
      categoriaHabitacionPaquete = 'Consultar';
      cantidadPersonasPaquete = 0;
    } else {
      ciudadOrigen = consultaPackage.packageOrigins?.[0]?.originExporta?.nombreCiudad || '';
      ciudadDestino = consultaPackage.packageDestinations?.[0]?.destinationCity?.nombreCiudad || '';
      provider = consultaPackage.providerName || '';
      numeroPaquete = consultaPackage.externalId || '';
      categoriaHabitacionPaquete = consultaPackage.hotelInformation?.[0]?.roomType || '';
      cantidadPersonasPaquete =
        occupationMapper[consultaPackage.hotelInformation?.[0]?.occupation] || 1;
    }

    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      nombreMail: `${formData.nombre} ${formData.apellido}`,
      email: formData.email,
      telefono: formData.telefono,
      comentarios: formData.comentarios,
      paisId,
      provider,
      nombreAgencia,
      tituloPaquete: consultaPackage.title || '',
      numeroPaquete,
      origenPaquete: ciudadOrigen,
      destinoPaquete: ciudadDestino,
      habitacionPaquete: 1,
      categoriaHabitacionPaquete,
      cantidadPersonasPaquete,
      precioTotalPaquete: consultaPackage.total || '',
      tipoConsulta: 'paquete',
      agenciaId: applicationId,
      emailAgencia: templateData?.header?.emailContacto || '',
      link: consultaPackage?.link || '',
    };
  };

  const prepareAgencyData = () => {
    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      email: formData.email,
      telefono: formData.telefono,
      consulta: formData.comentarios,
      nombreAgencia,
      tipoConsulta: 'oficina',
      emailAgencia: consultaPackage?.email || '',
    };
  };

  const prepareBannerData = () => {
    return {
      nombre: formData.nombre,
      apellido: formData.apellido,
      nombreMail: `${formData.nombre} ${formData.apellido}`,
      email: formData.email,
      telefono: formData.telefono,
      comentarios: formData.comentarios,
      paisId,
      provider: nombreAgencia,
      nombreAgencia,
      tituloPaquete: consultaPackage?.titulo || '',
      bannerId: consultaPackage?.id,
      bannerTitle: consultaPackage?.titulo,
      bannerDescription: consultaPackage?.descripcion,
      tipoConsulta: 'banners',
      agenciaId: applicationId,
      emailAgencia: templateData?.header?.emailContacto || '',
      id: consultaPackage?.id,
      proveedor: consultaPackage?.proveedor,
    };
  };

  const enviarConsulta = useCallback(
    async (e) => {
      e.preventDefault();

      if (!validateForm()) {
        return;
      }

      if (!token) {
        setError('Error: No hay token de autenticación disponible');
        return;
      }

      setIsSubmitting(true);
      setError(null);

      try {
        let data;
        let response;

        if (variant === 'agency') {
          data = prepareAgencyData();
          response = await ConsultasClient.enviarMailContacto(data, token);
        } else if (variant === 'banner') {
          data = prepareBannerData();
          response = await ConsultasClient.consultaMarcaBlanca(data, token);

          if (response.status === 201) {
            data.id = response.data.id;
            await ConsultasClient.enviarMailConsulta(data, token);
          }
        } else {
          data = preparePackageData();
          response = await ConsultasClient.consultaMarcaBlanca(data, token);

          if (response.status === 201) {
            data.id = response.data.id;
            await ConsultasClient.enviarMailConsulta(data, token);
          }
        }

        if (response.status === 200 || response.status === 201) {
          setShowSuccess(true);
          setTimeout(() => {
            handleClose();
          }, 2000);
        } else {
          setError('La consulta no se pudo enviar');
          setIsSubmitting(false);
        }
      } catch (error) {
        console.error('Error sending consultation:', error);
        setError('Error al enviar la consulta. Por favor intente nuevamente.');
        setIsSubmitting(false);
      }
    },
    [formData, consultaPackage, variant, token]
  );

  const handleCloseSnackbar = () => {
    setShowSuccess(false);
  };

  return (
    <>
      <Dialog
        open={!!consultaPackage}
        onClose={handleClose}
        maxWidth='xs'
        fullWidth
        disableScrollLock={false}
        PaperProps={{
          style: {
            borderRadius: 12,
          },
        }}
        slotProps={{
          backdrop: {
            style: { backgroundColor: 'rgba(0, 0, 0, 0.5)' },
          },
        }}
      >
        <DialogTitle
          sx={{
            paddingBottom: 0,
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            <Box
              display='flex'
              alignItems='center'
              gap={1}
              sx={{ width: '100%', position: 'relative' }}
            >
              <SendIcon style={{ color: background }} />
              <Typography variant='h6' sx={{ fontWeight: 600 }}>
                {title}
              </Typography>
              <IconButton
                onClick={handleClose}
                size='small'
                sx={{ position: 'absolute', right: 0 }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
          {subtitle && (
            <Box mt={1} color='text.secondary' fontSize='1.1rem' pt={2}>
              {subtitle}
            </Box>
          )}
        </DialogTitle>

        <DialogContent>
          <Box component='form' onSubmit={enviarConsulta} noValidate>
            <TextField
              fullWidth
              label={t('requestForm.name')}
              value={formData.nombre}
              onChange={handleInputChange('nombre')}
              margin='normal'
              required
              disabled={isSubmitting}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label={t('requestForm.lastName')}
              value={formData.apellido}
              onChange={handleInputChange('apellido')}
              margin='normal'
              required
              disabled={isSubmitting}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label='E-mail'
              type='email'
              value={formData.email}
              onChange={handleInputChange('email')}
              margin='normal'
              required
              disabled={isSubmitting}
              inputProps={{ maxLength: 50 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label={t('requestForm.phone')}
              value={formData.telefono}
              onChange={handleInputChange('telefono')}
              margin='normal'
              disabled={isSubmitting}
              inputProps={{ maxLength: 20 }}
              InputLabelProps={{
                shrink: true,
              }}
            />
            <TextField
              fullWidth
              label={t('requestForm.message')}
              multiline
              rows={4}
              value={formData.comentarios}
              onChange={handleInputChange('comentarios')}
              margin='normal'
              required
              disabled={isSubmitting}
              InputLabelProps={{
                shrink: true,
              }}
            />
            {error && (
              <Alert severity='error' sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={handleClose}
            variant='outlined'
            disabled={isSubmitting}
            sx={{
              backgroundColor: 'transparent',
              color: background,
              borderColor: background,
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.9,
                color: background,
              },
              '&:disabled': {
                color: background,
                borderColor: background,
                opacity: 0.5,
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={enviarConsulta}
            variant='contained'
            disabled={isSubmitting}
            startIcon={<SendIcon />}
            sx={{
              backgroundColor: background,
              color: text,
              '&:hover': {
                backgroundColor: background,
                opacity: 0.9,
              },
            }}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackbar
        open={showSuccess}
        message='La consulta se envió correctamente'
        onClose={handleCloseSnackbar}
        severity='info'
      />
    </>
  );
};

export default ContactFormModal;
